/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { downloadOutscanCpInscanReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { useNavigate } from "react-router-dom";

import { showErrorMessage } from "../../../../utils";

const HubOutscanVsCpInscanReportA3 = () => {
    const FILTER_STORAGE_KEY = "HUB_OUTSCAN_VS_CP_INSCAN_REPORT_A3_FILTERS";
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedDestinationHub, setSelectedDestinationHub] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [pendingCpSelection, setPendingCpSelection] = useState(null); // Store CP selection to restore after CP list loads
    const [currentUser, setCurrentUser] = useState({ name: "", email: "" });
    const [headerHubList, setHeaderHubList] = useState([]);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");
    const hubInitializedRef = useRef(false);

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Hub-Outscan Vs CP Inscan Report A3", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Helper function to save filters to sessionStorage
    const saveFiltersToStorage = () => {
        const filters = {
            dateRange: dateRange ? [
                dateRange[0]?.format("YYYY-MM-DD"),
                dateRange[1]?.format("YYYY-MM-DD")
            ] : null,
            selectedHubs,
            selectedDestinationHub,
            pagination: {
                current: pagination.current,
                pageSize: pagination.pageSize
            }
        };
        sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    };

    // Helper function to restore filters from sessionStorage
    const restoreFiltersFromStorage = () => {
        try {
            const savedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                    setDateRange([
                        moment(filters.dateRange[0]),
                        moment(filters.dateRange[1])
                    ]);
                }
                if (filters.selectedHubs && filters.selectedHubs.length > 0) {
                    setSelectedHubs(filters.selectedHubs);
                }
                // Store CP selection to restore after CP list is loaded
                if (filters.selectedDestinationHub && filters.selectedDestinationHub.length > 0) {
                    setPendingCpSelection(filters.selectedDestinationHub);
                }
                if (filters.pagination) {
                    setPagination(prev => ({
                        ...prev,
                        current: filters.pagination.current || prev.current,
                        pageSize: filters.pagination.pageSize || prev.pageSize
                    }));
                }
                // Clear the saved filters after restoring
                sessionStorage.removeItem(FILTER_STORAGE_KEY);
                return true;
            }
        } catch (error) {
            console.error("Error restoring filters:", error);
        }
        return false;
    };

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
                // Restore filters after hub list is loaded
                restoreFiltersFromStorage();
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (!selectedHubs || selectedHubs.length === 0) {
            setCpList([]); // clear CP list if no hub selected
            setSelectedDestinationHub([]); // Clear CP selection when no hubs selected
            setPendingCpSelection(null); // Clear pending CP selection
            return;
        }

        (async () => {
            try {
                const cpLists = await Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)));
                const allCps = [...new Set(cpLists.flat())];
                setCpList(allCps);
            } catch (error) {
                message.error("Failed to fetch CP list");
                setCpList([]);
            }
        })();
    }, [JSON.stringify(selectedHubs)]);

    // Restore CP selection after CP list is loaded
    useEffect(() => {
        if (pendingCpSelection && cpList.length > 0) {
            // Filter to only include CPs that exist in the current CP list
            const validCpIds = pendingCpSelection.filter(cpId =>
                cpList.some(cp => cp.value === cpId)
            );
            if (validCpIds.length > 0) {
                setSelectedDestinationHub(validCpIds);
            }
            setPendingCpSelection(null); // Clear pending selection after restoring
        }
    }, [cpList, pendingCpSelection]);

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([newActiveHubId]);
            } else {
                setSelectedHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Set default hub based on logged-in user — always enforced for hub-restricted roles
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        // let userid = JSON.parse(localStorage.getItem("userId") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const email = user?.username || "";
        let name = email.split("@")[0]?.replace(".", "_");

        /* if (userid?.userInfo?.firstName) {
            name = userid.userInfo.firstName.toLowerCase();
            if (userid?.userInfo?.lastName) {
                name += `_${userid.userInfo.lastName.toLowerCase()}`;
            }
        } */
        setCurrentUser({ name, email });

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        // Check if there are saved filters - if yes, don't set default hub (restore will handle it)
        const hasSavedFilters = sessionStorage.getItem(FILTER_STORAGE_KEY);

        if (!hubInitializedRef.current && !hasSavedFilters) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];

            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedHubs([]);
            } else {
                setSelectedHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]);

    const calendarRef = useRef();

    // Custom handler for date range change
    const handleDateRangeChange = (value) => {
        if (!value || !value[0] || !value[1]) {
            // If cleared, reset to today and open calendar
            setDateRange([moment(), moment()]);
            // Open the calendar dropdown
            if (calendarRef.current && calendarRef.current.picker) {
                calendarRef.current.picker.open();
            }
        } else {
            setDateRange(value);
        }
    };

    // Update useEffect to guard against invalid dateRange
    useEffect(() => {
        if (!hubUser) return;
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) return;
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) return;
        // If CPs are selected, wait for CP list to be loaded
        if (selectedDestinationHub && selectedDestinationHub.length > 0 && cpList.length === 0 && selectedHubs.length > 0) {
            return; // Wait for CP list to load
        }
        if (pendingCpSelection) return;
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, dateRange, pagination.current, pagination.pageSize, JSON.stringify(selectedHubs), JSON.stringify(selectedDestinationHub), pendingCpSelection]);

    const fetchReport = async () => {
        const requestId = createNewRequest();
        setLoading(true);
        let shouldUpdateState = true;
        try {
            const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
            if (!hasValidDates) {
                setLoading(false);
                return;
            }
            if (isLatestRequest(requestId.requestId)) {
                setReportData([]);
            }

            const queryParams = new URLSearchParams({
                fromDate_date: dateRange[0].format("YYYY-MM-DD"),
                toDate_date: dateRange[1].format("YYYY-MM-DD"),
                page: pagination.current,
                size: pagination.pageSize
            });

            // Resolve From Hubs (selectedHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedFromHubs = selectedHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedHubs;

            if (resolvedFromHubs && resolvedFromHubs.length > 0) {
                queryParams.append("from_hub_id", resolvedFromHubs.join(","));
            }
            if (selectedDestinationHub && selectedDestinationHub.length > 0) {
                queryParams.append("cp_id", selectedDestinationHub.join(","));
            }

            const url = `https://a3-framework.prayog.io/dynamic/t4_hub_outscan_vs_cp_inscan_report_agg_de_674b74?${queryParams.toString()}`;

            const body = {
                "user_name": "shriraj_bhosale",
                "user_email": "shriraj.bhosale@shreemaruti.com"
            };

            const fetchResponse = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer 571fd2db07f18177e5e2519fd937750b519cddcb65e04be8997180b3a766ca54"
                },
                body: JSON.stringify(body),
            });

            const responseData = await fetchResponse.json();
            const response = {
                response: {
                    data: responseData,
                    status: fetchResponse.status,
                    ok: fetchResponse.ok,
                },
            };

            if (!isLatestRequest(requestId.requestId)) {
                shouldUpdateState = false;
            } else {
                if (response?.response?.status === 200) {
                    const data = response?.response?.data?.data || [];
                    const count = response?.response?.data?.total_records || (Array.isArray(data) ? data.length : 0);
                    setReportData(data);
                    setPagination(prev => ({
                        ...prev,
                        total: count,
                    }));
                } else {
                    throw new Error(response?.message || "Failed to fetch report data");
                }
            }
        } catch (error) {
            console.error("fetchReport error:", error);
            if (shouldUpdateState && isLatestRequest(requestId.requestId)) {
                showErrorMessage("Failed to fetch report data");
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
            }
        } finally {
            if (shouldUpdateState && isLatestRequest(requestId.requestId)) {
                setLoading(false);
            }
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        // Placeholder for download functionality if needed, or remove button if not supported by new API
        message.info("Download not implemented for this report version yet.");
    };

    const navigate = useNavigate();

    // Handler for cell clicks (fetch details and navigate)
    const handleCellClick = async (type, record) => {
        // Save current filters before navigating
        saveFiltersToStorage();

        console.log(record, "record");
        const navigationState = {
            fromDate_date: record.date,
            toDate_date: record.date,
            from_hub_id: record.from_hub_id || record.fromHubId || record.hubId || record.hub_id,
            fromHubId: record.fromHubId || record.from_hub_id || record.hubId || record.hub_id,
            cp_id: record.cp_id || record.cpId || record.toCpId || record.to_cp_id || record.cp_id,
            cpId: record.cpId || record.cp_id || record.toCpId || record.to_cp_id || record.cpId,
            type: type
        };

        if (type === "outscanByHubToCp" || type === "inscanBySameCp" || type === "inscanByOtherCp") {
            navigate("/hub-outscan-vs-cp-inscan-clickable-a3", {
                state: navigationState
            });
        }
    };

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Hub-Outscan Vs CP Inscan Report A3" navigateTo="/reports" newReportUrl="/reports/hub-out-vs-cp-in/">
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From & To Date</div>
                        <CalendarComponent
                            ref={calendarRef}
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Origin Hub"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(value) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(value, setSelectedHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">CP Name</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select CP"
                            options={cpList}
                            onChange={(value) => {
                                setSelectedDestinationHub(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedDestinationHub}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    {/* <Col xs={24} sm={12} md={8} lg={6} style={{ textAlign: "right", paddingTop: "22px" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                        >
                            Download Report
                        </Button>
                    </Col> */}
                </Row>
                <Row style={{ marginTop: 16 }}>

                </Row>
            </Card>
            <TableComponent
                columns={[
                    { title: "Date", dataIndex: "date", key: "date", sorter: true, align: "center", width: 100, },
                    { title: "From Hub", dataIndex: "from_hub", key: "from_hub", sorter: true, align: "center", width: 160, },
                    { title: "To CP", dataIndex: "cp_name", key: "cp_name", sorter: true, align: "center", width: 160, },
                    {
                        title: "Outscan By Hub To CP",
                        dataIndex: "outscan_by_hub_to_cp",
                        key: "outscan_by_hub_to_cp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            <span
                                style={{
                                    color: "#1890ff",
                                    cursor: "pointer",
                                    textDecoration: "underline"
                                }}
                                onClick={() => handleCellClick("outscanByHubToCp", record)}
                            >
                                {text}
                            </span>
                        )
                    },
                    {
                        title: "Total Hub Outscan Weight (Kg)",
                        dataIndex: "total_outscan_weight",
                        key: "total_outscan_weight",
                        sorter: true,
                        align: "center",
                        width: 160,
                        render: (text) => (
                            <span>{text ? Number(text).toFixed(2) : "0.00"}</span>
                        )
                    },
                    {
                        title: "Inscan By Same CP",
                        dataIndex: "inscan_by_same_cp",
                        key: "inscan_by_same_cp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            <span
                                style={{
                                    color: text && text > 0 ? "#1890ff" : "black",
                                    cursor: text && text > 0 ? "pointer" : "default",
                                    textDecoration: text && text > 0 ? "underline" : "none"
                                }}
                                onClick={() => text && text > 0 && handleCellClick("inscanBySameCp", record)}
                            >
                                {text || 0}
                            </span>
                        )
                    },
                    {
                        title: "Inscan By Same CP Percentage",
                        dataIndex: "inscan_by_same_cp_percentage",
                        key: "inscan_by_same_cp_percentage",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text) => (
                            <span>{text}%</span>
                        )
                    },
                    {
                        title: "Inscan By Other CP",
                        dataIndex: "inscan_by_other_cp",
                        key: "inscan_by_other_cp",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text, record) => (
                            <span
                                style={{
                                    color: text && text > 0 ? "#1890ff" : "black",
                                    cursor: text && text > 0 ? "pointer" : "default",
                                    textDecoration: text && text > 0 ? "underline" : "none"
                                }}
                                onClick={() => text && text > 0 && handleCellClick("inscanByOtherCp", record)}
                            >
                                {text || 0}
                            </span>
                        )
                    },
                    {
                        title: "Inscan By Other CP Percentage",
                        dataIndex: "inscan_by_other_cp_percentage",
                        key: "inscan_by_other_cp_percentage",
                        sorter: true,
                        align: "center",
                        width: 10,
                        render: (text) => (
                            <span>{text}%</span>
                        )
                    },
                ]}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                rowKey={(record, index) => `${record.date}-${record.fromHubId}-${record.cpId}-${index}`}
                style={{ marginTop: 32 }}
            />
        </div>
    );
};

export default HubOutscanVsCpInscanReportA3;
